export type ApiErrorFields = Record<string, string[]>;

export const fieldLabels: Record<string, string> = {
  body: "请求内容",
  documentType: "单据类型",
  documentDate: "单据日期",
  customerId: "客户",
  supplierId: "供应商",
  productId: "产品",
  unitId: "单位",
  quantity: "数量",
  actualPriceKhr: "实际单价",
  actualAmountKhr: "金额",
  finalTotalKhr: "最终总额",
  paidAmountKhr: "已付金额",
  paymentMethod: "付款方式",
  accountId: "资金账户",
  settlementReason: "收付款原因",
  originalDocumentId: "原单",
  returnMode: "退货范围",
  lines: "明细",
  counts: "盘点数量",
  varianceReason: "盘点说明",
  reason: "操作原因",
};

export function formatFieldPath(path: string) {
  const parts = path.split(".");
  const offset = parts[0] === "document" ? 1 : 0;
  if (parts.length >= offset + 3 && parts[offset] === "lines" && /^\d+$/.test(parts[offset + 1])) {
    const line = Number(parts[offset + 1]) + 1;
    const fieldPath = parts.slice(offset + 2).join(".");
    const field = fieldLabels[fieldPath] ?? fieldLabels[parts[offset + 2]] ?? parts[offset + 2];
    return `第${line}行${field}`;
  }
  if (parts[offset] === "counts" && parts.length >= offset + 3) {
    return `产品 ${parts[offset + 1]} 的${fieldLabels.counts}（${fieldLabels.unitId} ${parts[offset + 2]}）`;
  }
  if (parts[offset] === "reasons" && parts.length >= offset + 2) {
    return `产品 ${parts[offset + 1]} 的${fieldLabels.varianceReason}`;
  }
  const fieldPath = parts.slice(offset).join(".");
  return fieldLabels[fieldPath] ?? fieldLabels[path] ?? path;
}

export function formatApiErrorMessage(
  operation: string | undefined,
  code: string,
  message: string,
  fields?: ApiErrorFields,
  requestId?: string,
) {
  const details = Object.entries(fields ?? {})
    .flatMap(([path, errors]) => errors.map((detail) => `${formatFieldPath(path)}：${detail}`))
    .slice(0, 6);
  const suffix = [
    details.length ? details.join("；") : "",
    requestId ? `请求编号：${requestId}` : "",
  ].filter(Boolean);
  const prefix = operation ? `${operation}：` : "";
  return `${prefix}${message}${suffix.length ? `（${suffix.join("；")}）` : ""} [${code}]`;
}

export function focusFirstErrorField(fields?: ApiErrorFields) {
  if (typeof document === "undefined" || !fields) return false;
  const namedElements = Array.from(document.querySelectorAll<HTMLElement>("[name]"));
  for (const path of Object.keys(fields)) {
    const candidates = [path, path.startsWith("document.") ? path.slice("document.".length) : ""];
    const target = namedElements.find((element) => {
      const name = element.getAttribute("name") ?? "";
      return candidates.some(
        (candidate) => candidate && (name === candidate || name.startsWith(`${candidate}.`)),
      );
    });
    if (target) {
      target.focus();
      return true;
    }
  }
  return false;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly baseMessage: string,
    public readonly status: number,
    public readonly fields: ApiErrorFields | undefined,
    public readonly requestId: string | undefined,
    public readonly path: string,
    public readonly operation?: string,
  ) {
    super(formatApiErrorMessage(operation, code, baseMessage, fields, requestId));
    this.name = "ApiError";
  }
}
